import {
  ALERT_DELIVERY_CHOICES, ALERT_DELIVERY_LABELS, DEFAULT_ALERT_DELIVERY, FACILITIES_CONTACT_ROLES, FACILITIES_CONTACT_ROLE_LABELS,
  isAlertDelivery, isFacilitiesContactRole, normalizeFacilitiesContacts,
  type AlertDelivery, type FacilitiesContactRole,
} from '@evi/shared';

/**
 * The facilities contacts card's draft rules (Warren, 28 Oct 2026, item 6).
 * The API is the authority (PUT /locations/:id/facilities-contacts runs the
 * same @evi/shared normalizeFacilitiesContacts); this keeps Save from
 * offering a write it would refuse, and says which row to fix.
 *
 * Every contact has a role (Warren, 28 Oct 2026: "Property manager,
 * Concierge, Facilities, Owner, Other"). The API keeps a contact's current
 * role when none is sent; the card always sends one, and a new row must
 * have one chosen before Save.
 */

export interface ContactDraft {
  name: string;
  email: string;
  /** '' until the person picks one on a new row. */
  role: FacilitiesContactRole | '';
  /** How often they hear about outages (predictive maintenance stage 4); Standard on a new row (Warren, 29 Oct 2026:
   *  quiet by default), never right away unless someone picks it. */
  alert_delivery: AlertDelivery;
}

export const ROLE_OPTIONS: ReadonlyArray<{ value: FacilitiesContactRole; label: string }> =
  FACILITIES_CONTACT_ROLES.map((value) => ({ value, label: FACILITIES_CONTACT_ROLE_LABELS[value] }));

export const DELIVERY_OPTIONS: ReadonlyArray<{ value: AlertDelivery; label: string }> =
  ALERT_DELIVERY_CHOICES.map((value) => ({ value, label: ALERT_DELIVERY_LABELS[value] }));
export const NEW_ROW_DELIVERY: AlertDelivery = DEFAULT_ALERT_DELIVERY;

export function deliveryLabel(delivery: string | null | undefined): string {
  return isAlertDelivery(delivery) ? ALERT_DELIVERY_LABELS[delivery] : ALERT_DELIVERY_LABELS[DEFAULT_ALERT_DELIVERY];
}

export function roleLabel(role: string): string {
  return isFacilitiesContactRole(role) ? FACILITIES_CONTACT_ROLE_LABELS[role] : FACILITIES_CONTACT_ROLE_LABELS.other;
}

/** Rows the person left entirely blank are dropped, not refused. */
export function draftToPayload(rows: ReadonlyArray<ContactDraft>): ContactDraft[] {
  return rows
    .map((r) => ({ name: r.name.trim(), email: r.email.trim(), role: r.role, alert_delivery: r.alert_delivery }))
    .filter((r) => r.name || r.email);
}

export type DraftCheck =
  | { ok: true; contacts: Array<{ name: string; email: string; role: FacilitiesContactRole; alert_delivery: AlertDelivery }> }
  | { ok: false; error: string };

export function checkDraft(rows: ReadonlyArray<ContactDraft>): DraftCheck {
  const contacts = draftToPayload(rows);
  const missing = contacts.findIndex((c) => !c.role);
  if (missing >= 0) return { ok: false, error: `Contact ${missing + 1}: choose a role.` };
  const result = normalizeFacilitiesContacts(contacts, { actorId: null, now: new Date() });
  if (!result.ok) return { ok: false, error: result.error };
  return { ok: true, contacts: result.contacts.map((c) => ({ name: c.name, email: c.email, role: c.role, alert_delivery: c.alert_delivery })) };
}

/** Plain words for where a contact came from. */
export function sourceLabel(source: string): string {
  if (source === 'admin') return 'Added in Admin';
  if (source.startsWith('invite:')) return 'Accepted an invite';
  if (source.startsWith('seed:') || source === 'seed') return 'From EV Initiative records';
  if (source.startsWith('import:') || source === 'import') return 'Imported by EV Initiative';
  return 'Added by EV Initiative';
}

/** The anchor and route an email links to: /locations/:id/facilities. */
export const FACILITIES_ANCHOR = 'facilities';
export function facilitiesPath(locationId: string): string {
  return `/locations/${locationId}/${FACILITIES_ANCHOR}`;
}
